import React, { FC, useState, useEffect, useMemo, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./courses.scss";
import CommonHeader from "../../../../shared/common-header/common-header";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  fetchCourses,
  fetchCoursesStatus,
  fetchCoursesCount,
  fetchCourseDeleteStatus,
  coursesActions,
  fetchCourseSyncOnActiveStatus,
  fetchUpdateCourseDataStatus,
  getCourseUpdatedData,
} from "../../../../reducers/coursesSlice";
import { fetchClubs, clubsActions, fetchClubsStatus } from "../../../../reducers/clubsSlice";
import { accountStorageActions } from "../../../../reducers/accountStorageSlice";
import { CoursesListParams } from "../../../../models/courses";
import { ClubPayload } from "../../../../models/clubs";
import CourseListItem from "../course-list-item/course-list-time";
import CourseListLoader from "../../../../shared/content-loaders/CourseListLoader";
import Pagination from "../../../../shared/pagination/pagination";
import {
  CLUB_ACTIVE_STATUS,
  COURSE_SYNC_STATUS_INIT_NAME,
  COURSE_SYNC_STATUS_SUCCESS_NAME,
  SAGA_STATUS_FAILED,
  SAGA_STATUS_PENDING,
  SAGA_STATUS_SUCCESS,
} from "../../../../utils/constants";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

interface CourseDisplayItem {
  _id: string;
  clubId: string;
  clubObjectId?: string;
  courseId?: number;
  distance: number;
  holes: number;
  par: number;
  rating: number;
  position?: number;
  slope: number;
  status: number;
  syncStatus?: string;
  addressOne?: string;
  addressTwo?: string;
  city?: string;
  name: string;
  location?: ClubPayload["location"];
  photos: NonNullable<ClubPayload["photos"]>;
  countryName?: string;
  provinceName?: string;
}

interface CoursesProps {}

const Courses: FC<CoursesProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const clubs: Array<ClubPayload> = useAppSelector(fetchClubs);
  const clubsListStatus = useAppSelector(fetchClubsStatus);
  const course = useAppSelector(fetchCourses);
  const coursesListedStatus = useAppSelector(fetchCoursesStatus);
  const totalCoursesCount = useAppSelector(fetchCoursesCount);
  const courseDeleteStatus = useAppSelector(fetchCourseDeleteStatus);
  const courseSyncStatus = useAppSelector(fetchCourseSyncOnActiveStatus);
  const courseUpdateStatus = useAppSelector(fetchUpdateCourseDataStatus);
  const courseUpdatedData = useAppSelector(getCourseUpdatedData);
  const { t } = useTranslation("");

  const [showPopup, SetShowPopup] = useState<boolean>(false);
  const [skip, setSkip] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleCloseWelcomeModal = () => SetShowPopup(false);

  const loadCourses = useCallback(
    (listSkip: number, searchTerm: string) => {
      const params: CoursesListParams = {
        isDeleted: false,
        skip: listSkip,
        limit: PAGE_SIZE,
        includeTotal: true,
      };
      if (searchTerm) {
        params.search = searchTerm;
      }
      dispatch(coursesActions.coursesList(params));
    },
    [dispatch]
  );

  const courseData = useMemo((): CourseDisplayItem[] => {
    return course
      .map(
        ({
          _id,
          clubObjectId,
          courseId,
          distance,
          holes,
          par,
          rating,
          position,
          slope,
          status,
          syncStatus,
        }): CourseDisplayItem | null => {
          const clubData = clubs.find(({ _id: clubId }) => clubId === clubObjectId);
          if (!clubData || !_id || !clubData._id) return null;

          const {
            addressOne,
            addressTwo,
            city,
            name,
            location,
            _id: clubId,
            photos,
            countryName,
            provinceName,
          } = clubData;

          return {
            _id,
            clubObjectId,
            courseId,
            distance: distance ?? 0,
            holes: holes ?? 0,
            par: par ?? 0,
            rating: rating ?? 0,
            position,
            addressOne,
            name: name ?? "-",
            location,
            addressTwo,
            city,
            clubId,
            photos: photos ?? [],
            slope: slope ?? 0,
            countryName,
            provinceName,
            status: status ?? 0,
            syncStatus,
          };
        }
      )
      .filter((item): item is CourseDisplayItem => item !== null);
  }, [course, clubs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setSkip(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadCourses(skip, debouncedSearch);
  }, [skip, debouncedSearch, loadCourses]);

  useEffect(() => {
    setPageCount(Math.ceil(totalCoursesCount / PAGE_SIZE));
  }, [totalCoursesCount]);

  useEffect(() => {
    if (courseSyncStatus === SAGA_STATUS_SUCCESS) {
      toast.info("Course Sync started.");
      dispatch(coursesActions.resetCourseSyncOnActiveStatus());
      dispatch(clubsActions.clubsList());
      loadCourses(skip, debouncedSearch);
    }
  }, [courseSyncStatus, debouncedSearch, dispatch, loadCourses, skip]);

  useEffect(() => {
    if (courseUpdateStatus === SAGA_STATUS_FAILED) {
      dispatch(clubsActions.clubsList());
      loadCourses(skip, debouncedSearch);
    }
  }, [courseUpdateStatus, debouncedSearch, dispatch, loadCourses, skip]);

  useEffect(() => {
    if (
      courseUpdatedData &&
      Object.keys(courseUpdatedData)?.length > 0 &&
      courseUpdatedData?.status === CLUB_ACTIVE_STATUS &&
      courseUpdateStatus === SAGA_STATUS_SUCCESS
    ) {
      if (
        ![COURSE_SYNC_STATUS_SUCCESS_NAME, COURSE_SYNC_STATUS_INIT_NAME].includes(
          courseUpdatedData?.syncStatus
        )
      ) {
        const syncCourseData = { courseObjectId: courseUpdatedData?._id };
        dispatch(coursesActions.courseSyncOnActive(syncCourseData));
      }
      dispatch(coursesActions.resetCourseUpdatedData());
      dispatch(clubsActions.clubsList());
      loadCourses(skip, debouncedSearch);
    }
  }, [
    courseUpdateStatus,
    courseUpdatedData,
    debouncedSearch,
    dispatch,
    loadCourses,
    skip,
  ]);

  useEffect(() => {
    if (coursesListedStatus === SAGA_STATUS_SUCCESS) {
      if (totalCoursesCount === 0 && !debouncedSearch) {
        SetShowPopup(true);
      }
      dispatch(coursesActions.resetFetchCourseStatus());
    }
  }, [coursesListedStatus, totalCoursesCount, debouncedSearch, dispatch]);

  useEffect(() => {
    dispatch(clubsActions.clubsList());
    return () => {
      dispatch(coursesActions.resetSelectedCourse());
    };
  }, [dispatch]);

  const isCoursesLoading = coursesListedStatus === SAGA_STATUS_PENDING;
  const isClubsLoading =
    clubsListStatus === SAGA_STATUS_PENDING && clubs.length === 0;
  const showCourseListLoader = isCoursesLoading || isClubsLoading;
  const showCourseEmptyState =
    !showCourseListLoader &&
    coursesListedStatus === SAGA_STATUS_SUCCESS &&
    courseData.length === 0;

  return (
    <>
      <div className="coursesWrapper">
        <CommonHeader sectionTitle={t("headerComponent.Listings")}></CommonHeader>
        <div className="container py-4 my-2">
          <div className="coursesToolbar">
            <div className="coursesSearch">
              <em className="fas fa-search coursesSearch__icon" aria-hidden="true"></em>
              <input
                type="search"
                className="form-control"
                placeholder={t("Courses.searchPlaceholder", "Search by course name")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t("Courses.searchPlaceholder", "Search by course name")}
              />
            </div>
            {totalCoursesCount > 0 && (
              <span className="coursesToolbar__count">
                {totalCoursesCount} {totalCoursesCount === 1 ? "course" : "courses"}
              </span>
            )}
          </div>

          {showCourseListLoader && <CourseListLoader count={4} />}

          {!showCourseListLoader && courseData.length > 0 && (
            <>
              {courseData.map(
                ({
                  _id,
                  clubId,
                  distance,
                  holes,
                  par,
                  slope,
                  rating,
                  addressOne,
                  addressTwo,
                  city,
                  name,
                  photos,
                  countryName,
                  provinceName,
                  status,
                  syncStatus,
                }) => (
                  <CourseListItem
                    clubId={clubId}
                    courseId={_id}
                    courseName={name}
                    address={`${addressOne || ""} ${addressTwo ? `${addressTwo},` : ""} ${city || ""}, ${provinceName || ""}, ${countryName || ""}`}
                    distance={distance}
                    holes={holes}
                    par={par}
                    rating={rating}
                    slope={slope}
                    key={_id}
                    photos={photos}
                    status={status}
                    syncStatus={syncStatus}
                    courseDeleteStatus={courseDeleteStatus}
                  />
                )
              )}
            </>
          )}

          {showCourseEmptyState && (
            <div className="d-flex align-items-center justify-content-center py-5">
              <h2 className="mb-0 text-center">
                {debouncedSearch
                  ? t("Courses.noSearchResults", "No courses match your search.")
                  : t("Courses.message")}
              </h2>
            </div>
          )}

          {totalCoursesCount > PAGE_SIZE && (
            <Pagination
              totalCount={totalCoursesCount}
              setSkip={setSkip}
              pageCount={pageCount}
            />
          )}
        </div>
      </div>
      <Modal
        className="welcomePopupModal"
        show={showPopup}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body>
          <img
            src="../assets/images/golf-course-placeholder.png"
            alt="golf-course"
            className="img-fluid d-block mx-auto"
          />
          <h2 className="mb-2 mt-3">{t("Courses.title")}</h2>
          <p className="mb-4 fw600">{t("Courses.description")}</p>
          <button
            type="button"
            className="button button-primary button-rounded fw700 mb-3"
            onClick={() => {
              dispatch(accountStorageActions.showHideCourseAddPopup(false));
              navigate("/register");
              handleCloseWelcomeModal();
            }}
          >
            {t("Courses.ListMyCourse")}
          </button>

          <div className="pt-3">
            <a
              href={void 0}
              className="_laterLink"
              onClick={() => {
                dispatch(accountStorageActions.showHideCourseAddPopup(false));
                handleCloseWelcomeModal();
              }}
            >
              {t("Courses.RemindMeLater")}
            </a>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Courses;
